import { useMemo } from "react";

import type { CustomFieldDef } from "#app/components/custom-fields/types";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { useAllCustomFields } from "#app/lib/hooks/use-all-custom-fields";
import {
  FORM_PROPERTIES,
  makePropertyField,
  propertyFieldId,
  type FormItem,
  type FormProperty,
} from "#app/lib/forms";

type Choice = { value: string; label: string };

type StagesByWorkflow = ReturnType<typeof useWorkspaceData>["stagesByWorkflow"];

function stageChoices(stagesByWorkflow: StagesByWorkflow, workflowIds: readonly string[]): Choice[] {
  return workflowIds.flatMap((workflowId) =>
    (stagesByWorkflow.get(workflowId) ?? []).map((stage) => ({
      label: stage.name ?? "",
      value: stage.stageId,
    })),
  );
}

/** Empty `stageWorkflowIds` offers every workflow's Stages. */
export function useFormFieldCatalog({
  stageWorkflowIds = [],
}: { stageWorkflowIds?: readonly string[] } = {}): ReadonlyMap<string, CustomFieldDef> {
  const fields = useAllCustomFields();
  const { issueSources, issueTypes, stagesByWorkflow, teams, workflows } = useWorkspaceData();
  // A caller's list is a fresh array each render; the joined key is what changes.
  const stageScope = stageWorkflowIds.join(",");

  return useMemo(() => {
    const liveWorkflows = workflows.filter((workflow) => !workflow.archived);
    const scoped = stageScope === "" ? [] : stageScope.split(",");
    const stageWorkflows = scoped.length > 0 ? scoped : liveWorkflows.map((row) => row.workflowId);
    const choices: Partial<Record<FormProperty, Choice[]>> = {
      issueSource: issueSources
        .filter((source) => !source.archived)
        .map((source) => ({ label: source.name, value: source.issueSourceId })),
      issueType: issueTypes.map((type) => ({ label: type.name, value: type.issueTypeId })),
      stage: stageChoices(stagesByWorkflow, stageWorkflows),
      teams: teams.map((team) => ({ label: team.teamName ?? "", value: team.teamId })),
      workflow: liveWorkflows.map((workflow) => ({
        label: workflow.name ?? "",
        value: workflow.workflowId,
      })),
    };
    const properties = FORM_PROPERTIES.map((property) =>
      makePropertyField(property, choices[property]),
    );
    return new Map<string, CustomFieldDef>(
      [...fields, ...properties].map((field) => [field.customFieldId, field]),
    );
  }, [fields, issueSources, issueTypes, stageScope, stagesByWorkflow, teams, workflows]);
}

export function useStageScopedCatalog(
  catalog: ReadonlyMap<string, CustomFieldDef>,
  workflowId: string | null,
): ReadonlyMap<string, CustomFieldDef> {
  const { stagesByWorkflow } = useWorkspaceData();
  return useMemo(() => {
    if (!workflowId) return catalog;
    const scoped = new Map(catalog);
    scoped.set(
      propertyFieldId("stage"),
      makePropertyField("stage", stageChoices(stagesByWorkflow, [workflowId])),
    );
    return scoped;
  }, [catalog, stagesByWorkflow, workflowId]);
}

/** Until workspace data lands a custom field reads as deleted, so callers draw skeletons while false. */
export function useFormCatalogKnown(items: readonly FormItem[]): boolean {
  const { customFieldsKnown, issueSourcesKnown, issueTypesKnown, isHydrated } =
    useWorkspaceData();
  const linked = items.some((item) => item.kind === "customField" || item.kind === "property");
  return !linked || (customFieldsKnown && issueSourcesKnown && issueTypesKnown && isHydrated);
}
